import { Box, Button } from '@material-ui/core';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { FORMS_VARIANTS } from '../../../constants/forms-variants';
import { INPUT_TYPES } from '../../../constants/input-types';
import { StepInput } from './step-input/step-input.component';

const createInitialStateFromFieldValues = fields => {
  return Object.fromEntries(
    Object.keys(fields).map(fieldKey => [fieldKey, fields[fieldKey].value])
  );
};

export const NEXT_STEP_BUTTON_TEST_ID = 'next-step-button';

const StepFormWrapper = ({
  buttonLabel,
  fields,
  isInputLabelVisible,
  isLoading,
  onStepChange,
  onSubmit,
  variant
}) => {
  const [step, setStep] = useState(0);
  const [inputsValues, setInputsValues] = useState(
    createInitialStateFromFieldValues(fields)
  );
  const [error, setError] = useState({ message: undefined, field: undefined });
  const { background, boxShadow, color } = FORMS_VARIANTS[variant];

  function validateInput(fieldName) {
    const currentField = fields[fieldName];
    const currentFieldValue = inputsValues[fieldName];
    const currentFieldValidators = currentField.validator;
    if (!currentFieldValidators) return '';
    const validatorError = currentFieldValidators(currentFieldValue);

    setError({
      message: validatorError?.errorMessage ?? '',
      field: fieldName
    });

    return validatorError?.errorMessage;
  }

  function handleInputChange(fieldName) {
    return function propagate(newValue) {
      setError({ message: '' });
      return setInputsValues(prev => ({
        ...prev,
        [fieldName]: newValue
      }));
    };
  }

  function handleStepJump(newStep) {
    return function propagate() {
      const fieldName = Object.keys(fields)[step];
      const inputError = validateInput(fieldName);
      if (isLoading) return;
      if (newStep > step && (error.message || inputError)) return;
      setStep(newStep);
    };
  }

  function handleNextStep() {
    const fieldName = Object.keys(fields)[step];
    const inputError = validateInput(fieldName);
    if (error.field === fieldName || inputError || isLoading) return;
    if (step === Object.keys(fields).length - 1) {
      onSubmit(inputsValues);
    } else setStep(prev => prev + 1);
  }

  useEffect(() => {
    onStepChange?.(step, inputsValues);
  }, [inputsValues, onStepChange, step]);

  return (
    <div className="step-form-wrapper">
      {Object.entries(fields).map(
        ([fieldName, { label, placeholder, type }], index) => {
          const isDisplayVisible = index > 0 ? !!inputsValues[fieldName] : true;
          const currentFieldError =
            error.field === fieldName ? error.message : '';
          return (
            <StepInput
              error={currentFieldError}
              isActive={step === index}
              isDisplayVisible={isDisplayVisible}
              isLabelVisible={isInputLabelVisible}
              isLoading={isLoading}
              key={fieldName}
              label={label}
              name={fieldName}
              onDisplayClick={handleStepJump(index)}
              onEnterKeyDown={handleNextStep}
              onInputChange={handleInputChange(fieldName)}
              placeholder={placeholder}
              type={type}
              value={inputsValues[fieldName]}
              variant={variant}
            />
          );
        }
      )}
      <Box boxShadow={boxShadow} borderRadius="25px">
        <Button
          data-testid={NEXT_STEP_BUTTON_TEST_ID}
          fullWidth
          type="button"
          variant="contained"
          onClick={handleNextStep}
          style={{ color, background }}
        >
          {buttonLabel}
        </Button>
      </Box>
    </div>
  );
};

StepFormWrapper.defaultProps = {
  buttonLabel: '',
  isInputLabelVisible: true,
  isLoading: false,
  onStepChange: null,
  variant: 'primary'
};

StepFormWrapper.propTypes = {
  buttonLabel: PropTypes.string,
  fields: PropTypes.objectOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      type: PropTypes.oneOf(Object.values(INPUT_TYPES)).isRequired,
      validator: PropTypes.func
    }).isRequired
  ).isRequired,
  isInputLabelVisible: PropTypes.bool,
  isLoading: PropTypes.bool,
  onStepChange: PropTypes.func,
  onSubmit: PropTypes.func.isRequired,
  variant: PropTypes.oneOf(Object.keys(FORMS_VARIANTS))
};

export default StepFormWrapper;
